import React, { useEffect, useRef } from 'react';
import { Clock, LogOut, RefreshCw } from 'lucide-react';
interface SessionTimeoutModalProps {
  secondsRemaining: number;
  onExtend: () => void;
  onLogout: () => void;
}
export function SessionTimeoutModal({
  secondsRemaining,
  onExtend,
  onLogout
}: SessionTimeoutModalProps) {
  const extendBtnRef = useRef<HTMLButtonElement>(null);
  // Auto-focus the extend button for accessibility
  useEffect(() => {
    extendBtnRef.current?.focus();
  }, []);
  // Keyboard: Escape extends session
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onExtend();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onExtend]);
  const progress = secondsRemaining / 60; // 0 to 1 over 60 seconds
  const circumference = 2 * Math.PI * 22; // radius 22
  const strokeDashoffset = circumference * (1 - progress);
  return <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200" role="alertdialog" aria-modal="true" aria-labelledby="session-timeout-title" aria-describedby="session-timeout-desc" data-id="element-2618">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8 max-w-sm w-full mx-4 shadow-2xl animate-in zoom-in-95 duration-300" data-id="element-2619">
        {/* Circular countdown */}
        <div className="flex justify-center mb-6" data-id="element-2620">
          <div className="relative w-20 h-20" data-id="element-2621">
            <svg className="w-20 h-20 -rotate-90" viewBox="0 0 48 48" data-id="element-2622">
              <circle cx="24" cy="24" r="22" fill="none" stroke="currentColor" strokeWidth="2" className="text-slate-800" data-id="element-2623" />
              <circle cx="24" cy="24" r="22" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className={secondsRemaining <= 10 ? 'text-red-500' : 'text-amber-500'} strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} style={{
              transition: 'stroke-dashoffset 1s linear'
            }} data-id="element-2624" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-2625">
              <span className={`text-xl font-bold tabular-nums ${secondsRemaining <= 10 ? 'text-red-400' : 'text-amber-400'}`} data-id="element-2626">
                {secondsRemaining}
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="text-center mb-6" data-id="element-2627">
          <h2 id="session-timeout-title" className="text-lg font-bold text-slate-100 mb-2 flex items-center justify-center gap-2" data-id="element-2628">
            <Clock size={18} className="text-amber-400" data-id="element-2629" />
            Sesión por expirar
          </h2>
          <p id="session-timeout-desc" className="text-sm text-slate-400 leading-relaxed" data-id="element-2630">
            Tu sesión se cerrará en{' '}
            <strong className={secondsRemaining <= 10 ? 'text-red-400' : 'text-amber-400'} data-id="element-2631">
              {secondsRemaining} segundos
            </strong>{' '}
            por inactividad.
          </p>
        </div>

        {/* Actions */}
        <div className="space-y-2.5" data-id="element-2632">
          <button ref={extendBtnRef} onClick={onExtend} className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 active:bg-indigo-700 text-white text-sm font-semibold py-2.5 rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-slate-900" data-id="element-2633">
            <RefreshCw size={16} data-id="element-2634" />
            Seguir trabajando
          </button>
          <button onClick={onLogout} className="w-full flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 active:bg-slate-750 text-slate-300 text-sm font-medium py-2.5 rounded-xl transition-all border border-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:ring-offset-slate-900" data-id="element-2635">
            <LogOut size={16} data-id="element-2636" />
            Cerrar sesión
          </button>
        </div>

        <p className="text-[11px] text-slate-600 text-center mt-4" data-id="element-2637">
          Presioná{' '}
          <kbd className="px-1.5 py-0.5 bg-slate-800 border border-slate-700 rounded text-slate-400 font-mono text-[10px]" data-id="element-2638">
            Esc
          </kbd>{' '}
          para continuar
        </p>
      </div>
    </div>;
}